import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { sectionMeta, visibleSections, sectionActionText, PortalSection } from '../../shared/portal-sections';
import { portalCoverStyle } from '../../shared/portal-cover';

@Component({
	selector: 'portal-project',
	templateUrl: './project.component.html',
	styleUrls: ['./project.component.scss']
})
export class ProjectComponent implements OnInit, OnDestroy {

	// The cover crop staff framed for this project's hero.
	coverStyle = portalCoverStyle;

	private sub?: Subscription;

	projectId!: string;
	project: any = null;
	company: any = null;
	contact: any = null;
	tiles: PortalSection[] = [];
	actions: { [key: string]: number } = {};
	loading = true;
	error = '';

	// Sections that have a real page built so far.
	private readonly built = new Set(['quotes', 'invoices', 'change_orders', 'sales_order', 'progress', 'documents']);

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private api: PortalApiService,
		private auth: PortalAuthService,
		private preview: PortalPreviewService
	) {}

	/**
	 * Which framing the hero shows: normally the project's own, but a cover preview is WeQuote's
	 * cover-image editor framing this page to show a crop that hasn't been saved yet, and that one
	 * wins for as long as the editor is open.
	 */
	get cover(): any {
		return this.preview.cover || this.project || {};
	}

	get statusLabel(): string {
		return this.project?.status ? this.project.status.charAt(0).toUpperCase() + this.project.status.slice(1) : '';
	}

	ngOnInit(): void {
		// Subscribed rather than read once from the snapshot: Angular reuses this
		// component when only the :id changes, so switching project (from the
		// activity feed, or with the browser's back button) would otherwise leave
		// the previous project's content on screen.
		this.sub = this.route.params.subscribe(params => {
			this.projectId = params['id'] || '';
			this.load();
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	load(): void {
		this.loading = true;
		this.error = '';
		this.api.project.get(this.projectId, (res: any) => {
			this.project = res?.project || null;
			this.company = res?.company || null;
			this.contact = res?.contact || null;
			this.tiles = visibleSections(res?.sections || []).map((key: string) => sectionMeta(key));
			this.actions = res?.actions || {};
			this.loading = false;
		}, (err: any) => {
			this.loading = false;
			if (err?.status === 401) { this.auth.goToLogin(); return; }
			this.error = err?.message || 'Could not load this project.';
		});
	}

	openTile(key: string): void {
		if (this.built.has(key)) this.router.navigate(['/project', this.projectId, key]);
	}

	isBuilt(key: string): boolean { return this.built.has(key); }

	// "Needs your attention" badge label for a tile (shared with the section nav).
	actionText(key: string): string {
		return sectionActionText(key, this.actions?.[key] || 0);
	}
}
